import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { postsAPI, usersAPI } from '../utils/api';
import PostCard from '../components/PostCard';
import StoriesBar from '../components/StoriesBar';
import { useAuth } from '../context/AuthContext';

export default function Home() {
  const { user }              = useAuth();
  const [posts, setPosts]     = useState([]);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [suggestions, setSuggestions] = useState([]);

  const loadPosts = useCallback(async (p = 1) => {
    try {
      const res = await postsAPI.getFeed(p);
      const newPosts = res.data.posts || [];
      setPosts(prev => p === 1 ? newPosts : [...prev, ...newPosts]);
      if (newPosts.length < 12) setHasMore(false);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPosts(1); }, [loadPosts]);
  useEffect(() => {
    usersAPI.getSuggestions().then(r => setSuggestions(r.data.users || [])).catch(() => {});
  }, []);

  const handleDelete = id => setPosts(p => p.filter(post => post.id !== id));

  const avatarPh = (name, size) => (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'linear-gradient(135deg,#E91E8C,#7B2FF7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: size * 0.38, flexShrink: 0 }}>
      {name?.[0]?.toUpperCase()}
    </div>
  );

  return (
    <div className="home-layout">
      {/* Feed */}
      <div className="feed-col">
        <StoriesBar />

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', marginTop: 'var(--sp-4)' }}>
            {[1, 2, 3].map(i => <PostSkeleton key={i} />)}
          </div>
        ) : posts.length === 0 ? (
          <EmptyFeed />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', marginTop: 'var(--sp-4)' }}>
            {posts.map(post => <PostCard key={post.id} post={post} onDelete={handleDelete} />)}
            {hasMore && (
              <button className="btn btn-outline w-full" onClick={() => { const next = page + 1; setPage(next); loadPosts(next); }}>
                Load more
              </button>
            )}
          </div>
        )}
      </div>

      {/* Right sidebar */}
      <div className="right-sidebar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-5)' }}>
          <Link to={`/profile/${user?.id}`}>
            {user?.avatar
              ? <img src={user.avatar} className="avatar" style={{ width: 46, height: 46 }} alt="" />
              : avatarPh(user?.full_name, 46)
            }
          </Link>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Link to={`/profile/${user?.id}`} className="font-bold truncate" style={{ display: 'block', fontSize: 'var(--fs-sm)' }}>{user?.username}</Link>
            <p style={{ color: 'var(--text-3)', fontSize: 'var(--fs-xs)' }} className="truncate">{user?.full_name}</p>
          </div>
        </div>

        {suggestions.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--sp-3)' }}>
              <span className="font-bold text-2" style={{ fontSize: 'var(--fs-xs)' }}>SUGGESTED FOR YOU</span>
              <Link to="/explore" style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--text)' }}>See All</Link>
            </div>
            {suggestions.map(s => <SuggestionRow key={s.id} user={s} />)}
          </div>
        )}

        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)', marginTop: 'var(--sp-5)', lineHeight: 1.7 }}>
          © 2024 MSocial ·{' '}
          <a href="https://noris.in" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-3)' }}>Noris Solutions</a>
        </p>
      </div>
    </div>
  );
}

function SuggestionRow({ user: u }) {
  const [following, setFollowing] = useState(false);
  const toggle = async () => {
    setFollowing(p => !p);
    try { const { usersAPI } = await import('../utils/api'); await usersAPI.toggleFollow(u.id); }
    catch { setFollowing(p => !p); }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
      <Link to={`/profile/${u.id}`}>
        {u.avatar
          ? <img src={u.avatar} className="avatar" style={{ width: 32, height: 32 }} alt="" />
          : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#E91E8C,#7B2FF7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{u.full_name?.[0]}</div>
        }
      </Link>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Link to={`/profile/${u.id}`} className="font-bold truncate" style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>{u.username}</Link>
        <p style={{ color: 'var(--text-3)', fontSize: 'var(--fs-xs)' }} className="truncate">{u.full_name}</p>
      </div>
      <button onClick={toggle} style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: following ? 'var(--text-2)' : 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>
        {following ? 'Following' : 'Follow'}
      </button>
    </div>
  );
}

function PostSkeleton() {
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 'var(--sp-3)' }}>
        <div className="skeleton" style={{ width: 38, height: 38, borderRadius: '50%' }} />
        <div style={{ flex: 1 }}>
          <div className="skeleton" style={{ width: '55%', height: 10, marginBottom: 7 }} />
          <div className="skeleton" style={{ width: '35%', height: 8 }} />
        </div>
      </div>
      <div className="skeleton" style={{ width: '100%', height: 'clamp(200px,40vw,320px)', borderRadius: 0 }} />
      <div style={{ padding: 'var(--sp-3)' }}>
        <div className="skeleton" style={{ width: '40%', height: 9, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: '65%', height: 9 }} />
      </div>
    </div>
  );
}

function EmptyFeed() {
  return (
    <div style={{ textAlign: 'center', padding: 'var(--sp-6) var(--sp-4)' }}>
      <div style={{ fontSize: 'clamp(40px,10vw,60px)', marginBottom: 'var(--sp-3)' }}>📸</div>
      <h3 style={{ marginBottom: 'var(--sp-2)' }}>Your feed is empty</h3>
      <p style={{ color: 'var(--text-2)', fontSize: 'var(--fs-sm)' }}>Follow people or explore to see posts here.</p>
      <Link to="/explore" className="btn btn-primary" style={{ marginTop: 'var(--sp-4)', display: 'inline-flex' }}>Explore</Link>
    </div>
  );
}
